import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { 
  ArrowLeft, MapPin, Home, Bed, Bath, Car, Ruler, Building2,
  Calendar, Shield, CheckCircle, Phone, Mail, MessageSquare, Send,
  ChevronLeft, ChevronRight, Sparkles, Youtube, Play, MessageCircle, ExternalLink
} from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import LocationInsights from "@/components/LocationInsights";
import PropertyValuation from "@/components/PropertyValuation";
import PropertyReviews from "@/components/PropertyReviews";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// WhatsApp icon component
const WhatsAppIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// Format description by removing emojis and converting to structured list
const formatDescription = (description) => {
  if (!description) return null;
  
  // Remove common emojis
  const emojiRegex = /[\u{1F300}-\u{1F9FF}]|[\u{2600}-\u{26FF}]|[\u{2700}-\u{27BF}]|[\u{1F600}-\u{1F64F}]|[\u{1F680}-\u{1F6FF}]|[\u{1F1E0}-\u{1F1FF}]|[\u{2300}-\u{23FF}]|[\u{2B50}]|[\u{1F4A0}-\u{1F4FF}]|[\u{1F500}-\u{1F5FF}]|[\u{1F900}-\u{1F9FF}]|[\u{1FA00}-\u{1FA6F}]|[\u{1FA70}-\u{1FAFF}]|[\u{231A}-\u{231B}]|[\u{23E9}-\u{23F3}]|[\u{23F8}-\u{23FA}]|[\u{25AA}-\u{25AB}]|[\u{25B6}]|[\u{25C0}]|[\u{25FB}-\u{25FE}]|[\u{2614}-\u{2615}]|[\u{2648}-\u{2653}]|[\u{267F}]|[\u{2693}]|[\u{26A1}]|[\u{26AA}-\u{26AB}]|[\u{26BD}-\u{26BE}]|[\u{26C4}-\u{26C5}]|[\u{26CE}]|[\u{26D4}]|[\u{26EA}]|[\u{26F2}-\u{26F3}]|[\u{26F5}]|[\u{26FA}]|[\u{26FD}]|[\u{2702}]|[\u{2705}]|[\u{2708}-\u{270D}]|[\u{270F}]|[\u{2712}]|[\u{2714}]|[\u{2716}]|[\u{271D}]|[\u{2721}]|[\u{2728}]|[\u{2733}-\u{2734}]|[\u{2744}]|[\u{2747}]|[\u{274C}]|[\u{274E}]|[\u{2753}-\u{2755}]|[\u{2757}]|[\u{2763}-\u{2764}]|[\u{2795}-\u{2797}]|[\u{27A1}]|[\u{27B0}]|[\u{27BF}]|[\u{2934}-\u{2935}]|[\u{2B05}-\u{2B07}]|[\u{2B1B}-\u{2B1C}]|[\u{2B55}]|[\u{3030}]|[\u{303D}]|[\u{3297}]|[\u{3299}]|[\u{FE0F}]/gu;
  
  let cleanText = description.replace(emojiRegex, '').trim();
  
  // Split by common delimiters like *, •, -, newlines
  const lines = cleanText
    .split(/[\n\r]+|\*{1,2}|•|(?<=[.!?])\s+(?=[A-Z])/)
    .map(line => line.trim())
    .filter(line => line.length > 3);
  
  // Identify potential headings (short lines ending with : or all caps)
  const sections = [];
  let currentSection = { heading: null, items: [] };
  
  lines.forEach(line => {
    // Check if it looks like a heading
    const isHeading = (line.endsWith(':') || line.length < 40) && 
                      (line.includes('Highlight') || line.includes('Feature') || 
                       line.includes('Detail') || line.includes('Size') || 
                       line.includes('Price') || line.includes('Rental') ||
                       line.includes('Launch') || line.includes('Investment') ||
                       line.includes('Booking') || line.includes('Contact') ||
                       line.includes('Call') || line.endsWith(':'));
    
    if (isHeading && line.length < 50) {
      if (currentSection.heading || currentSection.items.length > 0) {
        sections.push(currentSection);
      }
      currentSection = { heading: line.replace(/:$/, ''), items: [] };
    } else if (line.length > 5) {
      currentSection.items.push(line);
    }
  });
  
  if (currentSection.heading || currentSection.items.length > 0) {
    sections.push(currentSection);
  }
  
  // If no structured sections found, return as simple paragraphs
  if (sections.length === 0 || (sections.length === 1 && !sections[0].heading)) {
    return (
      <div className="text-slate-600 space-y-2">
        {lines.map((line, idx) => (
          <p key={idx}>{line}</p>
        ))}
      </div>
    );
  }
  
  return (
    <div className="space-y-4">
      {sections.map((section, sIdx) => (
        <div key={sIdx}>
          {section.heading && (
            <h4 className="font-bold text-[#0f2744] mb-2">{section.heading}</h4>
          )}
          {section.items.length > 0 && (
            <ul className="list-disc list-inside space-y-1 text-slate-600 ml-2">
              {section.items.map((item, iIdx) => (
                <li key={iIdx} className="leading-relaxed">{item}</li>
              ))}
            </ul>
          )}
        </div>
      ))}
    </div>
  );
};

export default function PropertyDetails() {
  const navigate = useNavigate();
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showInquiryForm, setShowInquiryForm] = useState(false);
  const [inquiryLoading, setInquiryLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [inquiryType, setInquiryType] = useState('inquiry'); // 'inquiry' or 'interest'
  const [inquiryForm, setInquiryForm] = useState({
    full_name: "",
    email: "",
    phone: "",
    message: "",
    budget_min: "",
    budget_max: "",
    preferred_contact: "phone"
  });

  const user = JSON.parse(localStorage.getItem("user") || "{}");

  useEffect(() => {
    fetchProperty();
  }, [id]);

  const fetchProperty = async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/properties/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProperty(response.data);
    } catch (error) {
      toast.error("Failed to load property details");
      navigate("/properties");
    } finally {
      setLoading(false);
    }
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    setInquiryLoading(true);
    
    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/inquiries/submit`,
        {
          ...inquiryForm,
          property_ids: [id],
          budget_min: inquiryForm.budget_min ? parseFloat(inquiryForm.budget_min) : null,
          budget_max: inquiryForm.budget_max ? parseFloat(inquiryForm.budget_max) : null,
          inquiry_type: inquiryType,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      const data = response.data;
      
      // Show simple success message
      toast.success(
        <div className="space-y-2">
          <p className="font-semibold">{inquiryType === 'interest' ? 'Interest sent successfully!' : 'Inquiry submitted successfully!'}</p>
          <p className="text-sm text-slate-600">
            {inquiryType === 'interest' && data.property_owner_whatsapp_links?.length > 0 
              ? `Admin and ${data.property_owner_whatsapp_links[0].name} have been notified.`
              : "Admin has been notified. Our team will contact you within 24 hours."
            }
          </p>
          <p className="text-xs text-slate-500 mt-2">
            Lead ID: {data.leads_created?.[0]?.lead_id?.slice(0, 8)}...
          </p>
        </div>,
        { duration: 5000 }
      );
      
      setShowInquiryForm(false);
      setInquiryForm({
        full_name: "",
        email: "",
        phone: "",
        message: "",
        budget_min: "",
        budget_max: "",
        preferred_contact: "phone"
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to submit inquiry");
    } finally {
      setInquiryLoading(false);
    }
  };

  // Generate WhatsApp link for direct contact
  const generateWhatsAppLink = (targetPhone = null, preferredContact = null) => {
    if (!property) return "#";
    const phone = targetPhone || '+919699136479'; // Default to admin
    
    // Build message with preferred contact method
    let contactPreference = '';
    if (preferredContact) {
      const contactMethods = {
        'phone': 'Phone Call',
        'email': 'Email',
        'whatsapp': 'WhatsApp'
      };
      contactPreference = `\n*Preferred Contact:* ${contactMethods[preferredContact] || preferredContact}`;
    }
    
    const message = `🏠 *Property Inquiry - CAPIN Realty*

*Property:* ${property.title}
*Type:* ${property.property_type?.charAt(0).toUpperCase() + property.property_type?.slice(1)} - ${property.property_subtype?.replace('_', ' ')}
*Location:* ${property.location}
*Price:* ₹${property.price?.toLocaleString('en-IN')}
*Area:* ${property.area} sq.ft${contactPreference}

Hi, I am interested in this property. Please share more details.`;
    
    // Format phone number
    let cleanPhone = phone.replace(/[^0-9]/g, '');
    if (cleanPhone.length === 10) {
      cleanPhone = '91' + cleanPhone;
    }
    
    const encodedMessage = encodeURIComponent(message);
    return `https://wa.me/${cleanPhone}?text=${encodedMessage}`;
  };

  // Check if property owner is a sourcing manager with contact
  const [propertyOwner, setPropertyOwner] = useState(null);
  
  useEffect(() => {
    const fetchPropertyOwner = async () => {
      if (property?.id) {
        try {
          const token = localStorage.getItem("token");
          const response = await axios.get(`${API}/properties/${property.id}/owner-contact`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          if (response.data.has_contact && response.data.owner) {
            setPropertyOwner(response.data.owner);
          }
        } catch (error) {
          console.log("Could not fetch property owner contact");
        }
      }
    };
    if (property) fetchPropertyOwner();
  }, [property]);

  const formatPrice = (price) => {
    if (price >= 10000000) {
      return `₹${(price / 10000000).toFixed(2)} Cr`;
    } else if (price >= 100000) {
      return `₹${(price / 100000).toFixed(2)} L`;
    }
    return `₹${price.toLocaleString('en-IN')}`;
  };

  const nextImage = () => {
    if (property?.media_files?.length > 0) {
      setCurrentImageIndex((prev) => (prev + 1) % property.media_files.length);
    }
  };

  const prevImage = () => {
    if (property?.media_files?.length > 0) {
      setCurrentImageIndex((prev) => (prev - 1 + property.media_files.length) % property.media_files.length);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="spinner" />
        </div>
      </Layout>
    );
  }

  if (!property) {
    return (
      <Layout>
        <div className="text-center py-12">
          <p className="text-slate-500">Property not found</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-6xl mx-auto fade-in">
        {/* Back Button */}
        <Button
          onClick={() => navigate("/properties")}
          variant="ghost"
          className="mb-6 -ml-4"
          data-testid="back-to-properties"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Properties
        </Button>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Main Content - Left Side */}
          <div className="lg:col-span-2 space-y-6">
            {/* Image Gallery */}
            <div className="bg-white rounded-lg overflow-hidden shadow-sm border border-slate-200">
              <div className="relative aspect-video bg-slate-100">
                {property.media_files && property.media_files.length > 0 ? (
                  <>
                    <img
                      src={property.media_files[currentImageIndex]}
                      alt={property.title}
                      className="w-full h-full object-cover"
                    />
                    {property.media_files.length > 1 && (
                      <>
                        <button
                          onClick={prevImage}
                          className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg"
                        >
                          <ChevronLeft className="h-6 w-6" />
                        </button>
                        <button
                          onClick={nextImage}
                          className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg"
                        >
                          <ChevronRight className="h-6 w-6" />
                        </button>
                        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2">
                          {property.media_files.map((_, idx) => (
                            <button
                              key={idx}
                              onClick={() => setCurrentImageIndex(idx)}
                              className={`w-2 h-2 rounded-full transition-all ${
                                idx === currentImageIndex ? 'bg-white w-6' : 'bg-white/50'
                              }`}
                            />
                          ))}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="w-full h-full flex items-center justify-center">
                    <Building2 className="h-24 w-24 text-slate-300" />
                  </div>
                )}
              </div>
              
              {/* Thumbnail Strip */}
              {property.media_files && property.media_files.length > 1 && (
                <div className="p-4 flex gap-2 overflow-x-auto">
                  {property.media_files.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => setCurrentImageIndex(idx)}
                      className={`flex-shrink-0 w-20 h-16 rounded overflow-hidden border-2 transition-all ${
                        idx === currentImageIndex ? 'border-amber-500' : 'border-transparent'
                      }`}
                    >
                      <img src={img} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* YouTube Video Section */}
            {property.youtube_video_url && (
              <div className="bg-white rounded-lg overflow-hidden shadow-sm border border-slate-200">
                <div className="p-4 border-b border-slate-200 flex items-center gap-3">
                  <div className="bg-red-100 p-2 rounded-lg">
                    <Youtube className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Property Video Tour</h3>
                    <p className="text-sm text-slate-500">Watch the video walkthrough of this property</p>
                  </div>
                </div>
                <div className="aspect-video">
                  <iframe
                    width="100%"
                    height="100%"
                    src={property.youtube_video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                    title="Property Video Tour"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    data-testid="youtube-video-embed"
                  ></iframe>
                </div>
              </div>
            )}

            {/* Property Plans Gallery */}
            {(property.floor_plan_image || property.project_plan_image || property.location_image || property.room_floor_plan_image || property.three_d_image || 
              (property.floor_plan_files?.length > 0) || (property.project_plan_files?.length > 0) || (property.location_map_files?.length > 0) ||
              (property.room_floor_plan_files?.length > 0) || (property.three_d_view_files?.length > 0) || (property.image_files?.length > 0) ||
              (property.video_files?.length > 0) || (property.external_links?.length > 0)) && (
              <div className="bg-white rounded-lg shadow-sm border border-slate-200 overflow-hidden" data-testid="property-plans-gallery">
                <div className="p-4 border-b border-slate-200 flex items-center gap-3">
                  <div className="bg-blue-100 p-2 rounded-lg">
                    <Ruler className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-slate-900">Property Plans, Media & Layout</h3>
                    <p className="text-sm text-slate-500">Floor plans, project layout, location map, 3D views, images & videos</p>
                  </div>
                </div>
                <div className="p-4 space-y-4">
                  {/* Legacy single-file plans */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                    {[
                      { url: property.floor_plan_image, label: 'Floor Plan' },
                      { url: property.project_plan_image, label: 'Project Plan' },
                      { url: property.location_image, label: 'Location Map' },
                      { url: property.room_floor_plan_image, label: 'Room Floor Plan' },
                      { url: property.three_d_image, label: '3D View' },
                    ].filter(p => p.url).map(({ url, label }) => (
                      <a
                        key={label}
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="group relative aspect-[4/3] rounded-lg overflow-hidden border border-slate-200 hover:border-[#c9a227] transition-all hover:shadow-md"
                        data-testid={`plan-view-${label.toLowerCase().replace(/\s/g, '-')}`}
                      >
                        <img src={url} alt={label} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                          <p className="text-white text-xs font-medium">{label}</p>
                        </div>
                      </a>
                    ))}
                  </div>

                  {/* New multi-file media categories */}
                  {[
                    { files: property.floor_plan_files, label: 'Floor Plans' },
                    { files: property.project_plan_files, label: 'Project Plans' },
                    { files: property.location_map_files, label: 'Location Maps' },
                    { files: property.room_floor_plan_files, label: 'Room Floor Plans' },
                    { files: property.three_d_view_files, label: '3D Views' },
                    { files: property.image_files, label: 'Property Images' },
                    { files: property.video_files, label: 'Videos' },
                  ].filter(cat => cat.files?.length > 0).map(({ files, label }) => (
                    <div key={label}>
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">{label} ({files.length})</h4>
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                        {files.map((file, idx) => {
                          const token = localStorage.getItem('token');
                          const fileUrl = `${process.env.REACT_APP_BACKEND_URL}/api/storage/files/${file.storage_path}?auth=${token}`;
                          const isImage = ['jpg','jpeg','png','gif','webp'].includes((file.original_filename || '').split('.').pop().toLowerCase());
                          const isVideo = ['mp4','mov','webm','avi'].includes((file.original_filename || '').split('.').pop().toLowerCase());
                          return (
                            <a key={idx} href={fileUrl} target="_blank" rel="noopener noreferrer" className="group relative aspect-square rounded-lg overflow-hidden border border-slate-200 hover:border-[#c9a227] transition-all hover:shadow-md bg-slate-50" data-testid={`media-file-${label.toLowerCase().replace(/\s/g, '-')}-${idx}`}>
                              {isImage ? (
                                <img src={fileUrl} alt={file.original_filename} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" loading="lazy" />
                              ) : isVideo ? (
                                <video src={fileUrl} className="w-full h-full object-cover" preload="metadata" />
                              ) : (
                                <div className="w-full h-full flex items-center justify-center"><span className="text-xs text-slate-500">{file.original_filename}</span></div>
                              )}
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-1.5">
                                <p className="text-white text-[10px] truncate">{file.original_filename}</p>
                              </div>
                              {file.compressed && <span className="absolute top-1 left-1 bg-green-500 text-white text-[8px] px-1 py-0.5 rounded-full">Compressed</span>}
                            </a>
                          );
                        })}
                      </div>
                    </div>
                  ))}

                  {/* External Links */}
                  {property.external_links?.length > 0 && (
                    <div>
                      <h4 className="text-sm font-semibold text-slate-700 mb-2">External Links ({property.external_links.length})</h4>
                      <div className="space-y-1">
                        {property.external_links.map((link, idx) => (
                          <a key={idx} href={link.url} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-blue-600 hover:underline p-2 bg-blue-50 rounded" data-testid={`external-link-${idx}`}>
                            <ExternalLink className="h-4 w-4 flex-shrink-0" />
                            <span className="truncate">{link.title || link.url}</span>
                          </a>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Property Info */}
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-xs px-2 py-1 bg-blue-100 text-blue-800 rounded uppercase font-medium">
                      {property.property_type}
                    </span>
                    <span className="text-xs px-2 py-1 bg-amber-100 text-amber-800 rounded uppercase font-medium">
                      For {property.purpose}
                    </span>
                    <span className={`text-xs px-2 py-1 rounded uppercase font-medium ${
                      property.status === 'available' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                    }`}>
                      {property.status}
                    </span>
                  </div>
                  <h1 className="text-2xl font-semibold text-slate-900" style={{ fontFamily: 'Playfair Display, serif' }}>
                    {property.title}
                  </h1>
                  <p className="text-slate-600 flex items-center gap-1 mt-2">
                    <MapPin className="h-4 w-4" />
                    {property.location}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold text-blue-700">{formatPrice(property.price)}</p>
                  {property.rate_amount && (
                    <p className="text-sm text-slate-500">+ {formatPrice(property.rate_amount)}/{property.rate || 'month'}</p>
                  )}
                </div>
              </div>

              {/* Description Section */}
              <div className="mb-6 p-4 bg-slate-50 rounded-lg border border-slate-100">
                <h3 className="font-bold text-[#0f2744] text-lg mb-3">Description</h3>
                {formatDescription(property.description)}
              </div>

              {/* Quick Stats */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                {property.area && (
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Ruler className="h-5 w-5 text-slate-500" />
                    <div>
                      <p className="text-sm text-slate-500">Carpet Area</p>
                      <p className="font-semibold">{property.area} sq ft</p>
                    </div>
                  </div>
                )}
                {property.bedrooms && (
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Bed className="h-5 w-5 text-slate-500" />
                    <div>
                      <p className="text-sm text-slate-500">Bedrooms</p>
                      <p className="font-semibold">{property.bedrooms} BHK</p>
                    </div>
                  </div>
                )}
                {property.bathrooms && (
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Bath className="h-5 w-5 text-slate-500" />
                    <div>
                      <p className="text-sm text-slate-500">Bathrooms</p>
                      <p className="font-semibold">{property.bathrooms}</p>
                    </div>
                  </div>
                )}
                {property.parking && (
                  <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                    <Car className="h-5 w-5 text-slate-500" />
                    <div>
                      <p className="text-sm text-slate-500">Parking</p>
                      <p className="font-semibold">{property.parking}</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Tabs */}
              <div className="mb-6">
                <div className="flex gap-1 bg-slate-100 p-1 rounded-xl">
                  {[
                    { key: 'details', label: 'Details', icon: 'Home' },
                    { key: 'measurements', label: 'Measurements', icon: 'Ruler' },
                    { key: 'insights', label: 'AI Insights', icon: 'Sparkles' }
                  ].map((tab) => {
                    const IconComp = tab.key === 'details' ? Home : tab.key === 'measurements' ? Ruler : Sparkles;
                    return (
                      <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        data-testid={`tab-${tab.key}`}
                        className={`flex-1 flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-semibold transition-all ${
                          activeTab === tab.key
                            ? 'bg-[#0f2744] text-white shadow-md'
                            : 'text-slate-600 hover:bg-white hover:text-[#0f2744] hover:shadow-sm'
                        }`}
                      >
                        <IconComp className="h-4 w-4" />
                        <span className="hidden sm:inline">{tab.label}</span>
                        <span className="sm:hidden text-xs">{tab.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Tab Content */}
              {activeTab === 'details' && (
                <div className="space-y-6">
                  {/* Property Details Grid */}
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {property.built_up_area && (
                      <div><p className="text-sm text-slate-500">Built-up Area</p><p className="font-medium">{property.built_up_area} sq ft</p></div>
                    )}
                    {property.super_built_up_area && (
                      <div><p className="text-sm text-slate-500">Super Built-up Area</p><p className="font-medium">{property.super_built_up_area} sq ft</p></div>
                    )}
                    {property.floor_number && (
                      <div><p className="text-sm text-slate-500">Floor</p><p className="font-medium">{property.floor_number} of {property.total_floors || 'N/A'}</p></div>
                    )}
                    {property.facing && (
                      <div><p className="text-sm text-slate-500">Facing</p><p className="font-medium capitalize">{property.facing}</p></div>
                    )}
                    {property.furnishing && (
                      <div><p className="text-sm text-slate-500">Furnishing</p><p className="font-medium capitalize">{property.furnishing}</p></div>
                    )}
                    {property.age_of_property && (
                      <div><p className="text-sm text-slate-500">Age of Property</p><p className="font-medium capitalize">{property.age_of_property.replace(/_/g, ' ')}</p></div>
                    )}
                    {property.possession_status && (
                      <div><p className="text-sm text-slate-500">Possession</p><p className="font-medium capitalize">{property.possession_status.replace(/_/g, ' ')}</p></div>
                    )}
                    {property.ownership_type && (
                      <div><p className="text-sm text-slate-500">Ownership</p><p className="font-medium capitalize">{property.ownership_type}</p></div>
                    )}
                  </div>

                  {/* Amenities */}
                  {property.amenities && property.amenities.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-3">Amenities</h3>
                      <div className="flex flex-wrap gap-2">
                        {property.amenities.map((amenity, idx) => (
                          <span key={idx} className="flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                            <CheckCircle className="h-3 w-3" />
                            {amenity}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Property Features */}
                  {property.property_features && property.property_features.length > 0 && (
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-3">Features</h3>
                      <div className="flex flex-wrap gap-2">
                        {property.property_features.map((feature, idx) => (
                          <span key={idx} className="flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-700 rounded-full text-sm">
                            <CheckCircle className="h-3 w-3" />
                            {feature}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* RERA Info */}
                  {property.rera_registered && (
                    <div className="p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="flex items-center gap-2">
                        <Shield className="h-5 w-5 text-green-600" />
                        <span className="font-medium text-green-800">RERA Registered</span>
                      </div>
                      {property.rera_id && (
                        <p className="text-sm text-green-700 mt-1">RERA ID: {property.rera_id}</p>
                      )}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'measurements' && (
                <div className="space-y-6">
                  {/* Room Measurements */}
                  {property.room_measurements && property.room_measurements.length > 0 ? (
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-3">Room Sizes</h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {property.room_measurements.map((room, idx) => (
                          <div key={idx} className="p-3 bg-slate-50 rounded-lg flex items-center justify-between">
                            <span className="font-medium">{room.room_type}</span>
                            <span className="text-slate-600">
                              {room.area || `${room.length} × ${room.width}`} {room.unit === 'sq_m' ? 'sq m' : 'sq ft'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <p className="text-slate-500 text-center py-8">Room measurements not available</p>
                  )}

                  {/* Plot Dimensions */}
                  {property.plot_dimensions && (property.plot_dimensions.area || property.plot_dimensions.length) && (
                    <div>
                      <h3 className="font-semibold text-slate-900 mb-3">Plot Dimensions</h3>
                      <div className="p-4 bg-slate-50 rounded-lg">
                        <div className="grid grid-cols-3 gap-4 text-center">
                          {property.plot_dimensions.length && (
                            <div>
                              <p className="text-sm text-slate-500">Length</p>
                              <p className="font-semibold">{property.plot_dimensions.length}</p>
                            </div>
                          )}
                          {property.plot_dimensions.width && (
                            <div>
                              <p className="text-sm text-slate-500">Width</p>
                              <p className="font-semibold">{property.plot_dimensions.width}</p>
                            </div>
                          )}
                          {property.plot_dimensions.area && (
                            <div>
                              <p className="text-sm text-slate-500">Total Area</p>
                              <p className="font-semibold">{property.plot_dimensions.area} {property.plot_dimensions.unit === 'sq_m' ? 'sq m' : 'sq ft'}</p>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'insights' && (
                <div className="space-y-8">
                  <PropertyValuation 
                    location={property.location}
                    propertyType={property.property_type}
                    carpetArea={property.area}
                    builtUpArea={property.built_up_area}
                    bedrooms={property.bedrooms}
                    bathrooms={property.bathrooms}
                    floorNumber={property.floor_number}
                    totalFloors={property.total_floors}
                    ageOfProperty={property.age_of_property}
                    furnishing={property.furnishing}
                    amenities={property.amenities}
                  />
                  <hr className="border-slate-200" />
                  <LocationInsights 
                    location={property.location}
                    propertyType={property.property_type}
                    purpose={property.purpose}
                  />
                </div>
              )}
            </div>
          </div>

          {/* Sidebar - Right Side */}
          <div className="space-y-6">
            {/* Inquiry Card */}
            <div className="bg-white rounded-lg shadow-sm border border-slate-200 p-6 sticky top-6">
              <h3 className="text-lg font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>
                Interested in this property?
              </h3>
              
              {!showInquiryForm ? (
                <div className="space-y-3">
                  <Button
                    onClick={() => { setInquiryType('inquiry'); setShowInquiryForm(true); }}
                    className="w-full bg-[#0f2744] hover:bg-[#1e3a5f] text-white rounded-lg h-12"
                    data-testid="show-inquiry-form"
                  >
                    <Send className="h-4 w-4 mr-2" />
                    Send Inquiry
                  </Button>
                  
                  <Button
                    onClick={() => { setInquiryType('interest'); setShowInquiryForm(true); }}
                    className="w-full h-12 bg-[#c9a227] hover:bg-[#ae8220] text-white rounded-lg font-semibold shadow-md hover:shadow-lg transition-all"
                    data-testid="send-interest-btn"
                  >
                    <Send className="h-5 w-5 mr-2" />
                    Send Interest
                  </Button>
                  
                  <div className="text-xs text-slate-500 space-y-1 pt-1">
                  </div>
                </div>
              ) : (
                <form onSubmit={handleInquirySubmit} className="space-y-4">
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`w-2 h-2 rounded-full ${inquiryType === 'interest' ? 'bg-[#c9a227]' : 'bg-[#0f2744]'}`}></span>
                    <span className="text-sm font-semibold text-slate-700">
                      {inquiryType === 'interest' ? 'Send Interest' : 'Send Inquiry'}
                    </span>
                  </div>
                  <div>
                    <Label htmlFor="full_name">Full Name *</Label>
                    <Input
                      id="full_name"
                      value={inquiryForm.full_name}
                      onChange={(e) => setInquiryForm(prev => ({ ...prev, full_name: e.target.value }))}
                      placeholder="Your name"
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      value={inquiryForm.phone}
                      onChange={(e) => setInquiryForm(prev => ({ ...prev, phone: e.target.value }))}
                      placeholder="+91 98765 43210"
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email *</Label>
                    <Input
                      id="email"
                      type="email"
                      value={inquiryForm.email}
                      onChange={(e) => setInquiryForm(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="your@email.com"
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Preferred Contact</Label>
                    <div className="flex gap-2 mt-1">
                      {[
                        { value: 'phone', icon: Phone },
                        { value: 'email', icon: Mail },
                        { value: 'whatsapp', icon: MessageSquare }
                      ].map(({ value, icon: Icon }) => (
                        <button
                          key={value}
                          type="button"
                          onClick={() => setInquiryForm(prev => ({ ...prev, preferred_contact: value }))}
                          className={`flex-1 flex items-center justify-center gap-1 p-2 rounded border-2 text-sm transition-all ${
                            inquiryForm.preferred_contact === value
                              ? 'border-amber-500 bg-blue-50 text-blue-700'
                              : 'border-slate-200'
                          }`}
                        >
                          <Icon className="h-4 w-4" />
                        </button>
                      ))}
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="message">Message</Label>
                    <Textarea
                      id="message"
                      value={inquiryForm.message}
                      onChange={(e) => setInquiryForm(prev => ({ ...prev, message: e.target.value }))}
                      placeholder="I'm interested in this property..."
                      rows={3}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label>Budget Range</Label>
                    <div className="flex gap-2 mt-1">
                      <Input
                        type="number"
                        value={inquiryForm.budget_min}
                        onChange={(e) => setInquiryForm(prev => ({ ...prev, budget_min: e.target.value }))}
                        placeholder="Min (e.g. 5000000)"
                        className="flex-1"
                        data-testid="budget-min-input"
                      />
                      <span className="flex items-center text-slate-400">to</span>
                      <Input
                        type="number"
                        value={inquiryForm.budget_max}
                        onChange={(e) => setInquiryForm(prev => ({ ...prev, budget_max: e.target.value }))}
                        placeholder="Max (e.g. 8000000)"
                        className="flex-1"
                        data-testid="budget-max-input"
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => setShowInquiryForm(false)}
                      className="flex-1 rounded-sm"
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={inquiryLoading}
                      className={`flex-1 text-white rounded-sm ${inquiryType === 'interest' ? 'bg-[#c9a227] hover:bg-[#ae8220]' : 'bg-[#0f2744] hover:bg-[#1e3a5f]'}`}
                      data-testid="submit-property-inquiry"
                    >
                      {inquiryLoading ? "Sending..." : (inquiryType === 'interest' ? "Send Interest" : "Submit Inquiry")}
                    </Button>
                  </div>
                </form>
              )}
            </div>

            {/* Price Breakdown */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">
              <div className="bg-gradient-to-r from-[#0f2744] to-[#1e3a5f] text-white p-4">
                <h3 className="text-lg font-semibold">Price Breakdown</h3>
              </div>
              <div className="p-6 space-y-3">
                {/* All-Inclusive Price (Total) */}
                <div className="flex justify-between items-center bg-[#fffdf5] -mx-6 px-6 py-3 border-b border-[#c9a227]">
                  <span className="text-lg font-bold text-[#0f2744]">All-Inclusive Price</span>
                  <span className="text-xl font-bold text-[#c9a227]">
                    {formatPrice(property.all_inclusive_price || property.price)}
                  </span>
                </div>

                {/* Carpet Area */}
                <div className="flex justify-between items-center pt-2">
                  <span className="text-slate-600 font-medium">Carpet Area</span>
                  <span className="font-semibold text-[#0f2744]">{property.area} sq.ft</span>
                </div>
                
                {/* Agreement Value */}
                {property.agreement_value && (
                  <div className="flex justify-between items-center">
                    <span className="text-slate-600">Agreement Value (Registered)</span>
                    <span className="font-medium">{formatPrice(property.agreement_value)}</span>
                  </div>
                )}
                
                <hr className="border-slate-200 my-3" />
                
                {/* Government Taxes */}
                <div className="space-y-2">
                  <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Government Taxes</span>
                  <div className="flex justify-between items-center text-sm pl-2">
                    <span className="text-slate-500">Stamp Duty</span>
                    <span className="text-slate-600">
                      {property.stamp_duty_percent || 7}%
                      {property.agreement_value && (
                        <span className="text-slate-400 ml-1">
                          ({formatPrice(property.agreement_value * (property.stamp_duty_percent || 7) / 100)})
                        </span>
                      )}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm pl-2">
                    <span className="text-slate-500">GST</span>
                    <span className="text-slate-600">
                      {property.gst_percent || (property.property_type === 'commercial' ? 12 : 5)}%
                      {property.agreement_value && (
                        <span className="text-slate-400 ml-1">
                          ({formatPrice(property.agreement_value * (property.gst_percent || (property.property_type === 'commercial' ? 12 : 5)) / 100)})
                        </span>
                      )}
                    </span>
                  </div>
                </div>
                
                <hr className="border-slate-200 my-3" />
                
                {/* Other Costs */}
                {(property.maintenance_per_sqft || property.corpus_fund || property.infrastructure_cost) && (
                  <>
                    <div className="space-y-2">
                      <span className="text-xs font-bold text-slate-400 uppercase tracking-wider">Other Costs</span>
                      {/* Maintenance */}
                      {property.maintenance_per_sqft && (
                        <div className="flex justify-between items-center pl-2">
                          <span className="text-slate-600">Maintenance</span>
                          <span className="font-medium">₹{property.maintenance_per_sqft?.toLocaleString('en-IN')}/sq.ft</span>
                        </div>
                      )}
                      
                      {/* Corpus Fund */}
                      {property.corpus_fund && (
                        <div className="flex justify-between items-center pl-2">
                          <span className="text-slate-600">Corpus Fund</span>
                          <span className="font-medium">{formatPrice(property.corpus_fund)}</span>
                        </div>
                      )}
                      
                      {/* Infrastructure Cost */}
                      {property.infrastructure_cost && (
                        <div className="flex justify-between items-center pl-2">
                          <span className="text-slate-600">Infrastructure Cost</span>
                          <span className="font-medium">{formatPrice(property.infrastructure_cost)}</span>
                        </div>
                      )}
                    </div>
                    <hr className="border-slate-200 my-3" />
                  </>
                )}
                
                {/* Calculated Rate per sqft */}
                {property.area && (
                  <div className="flex justify-between items-center bg-[#0f2744] text-white -mx-6 px-6 py-4 mt-4">
                    <span className="font-medium">Calculated Rate per sq.ft</span>
                    <span className="text-xl font-bold">
                      ₹{property.rate_per_sqft?.toLocaleString('en-IN') || Math.round((property.all_inclusive_price || property.price) / parseInt(property.area)).toLocaleString('en-IN')}/sq.ft
                    </span>
                  </div>
                )}
                
                {/* Monthly Rent (if applicable) */}
                {property.rate_amount && (
                  <div className="flex justify-between items-center pt-2 border-t border-slate-200">
                    <span className="text-slate-600">Monthly Rent</span>
                    <span className="font-semibold text-green-600">{formatPrice(property.rate_amount)}/month</span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="mt-8">
          <PropertyReviews propertyId={id} propertyTitle={property?.title} />
        </div>
      </div>
    </Layout>
  );
}
